import type * as React from 'react'
import {
  Tooltip,
  TooltipArrow,
  TooltipContent,
  TooltipTrigger,
} from '../../overlays/tooltip/tooltip'
import { getVisibleBlockedSlotMetrics } from './blocked-slot-layout'
import { FULL_PCT, MIN_BLOCKED_H } from './constants'
import type { BlockedSlot, PositionedBlockedSlot } from './types'
import { formatDuration } from './utils'

const OFF_HOURS_PREFIX = '__off-'

interface BlockedSlotCardProps {
  slot: PositionedBlockedSlot
  startTime: number
  totalHours: number
  pixelsPerMinute: number
  onBlockedSlotClick?: (slot: BlockedSlot) => void
}

export const BlockedSlotCard = ({
  slot,
  startTime,
  totalHours,
  pixelsPerMinute,
  onBlockedSlotClick,
}: BlockedSlotCardProps): React.JSX.Element | null => {
  const visibleMetrics = getVisibleBlockedSlotMetrics({
    startTime: slot.startTime,
    endTime: slot.endTime,
    visibleStartHour: startTime,
    totalHours,
  })

  if (!visibleMetrics) return null

  const widthPct = FULL_PCT / slot.totalColumns
  const leftPct = slot.column * widthPct
  const friendlyDuration = formatDuration(slot.startTime, slot.endTime)

  const isUserDefined = !slot.id.startsWith(OFF_HOURS_PREFIX)
  const isClickable = isUserDefined && !!onBlockedSlotClick

  const ariaLabel = slot.reason ? `Bloqueado: ${slot.reason}` : 'Horário bloqueado'
  const sharedStyle = {
    top: `${visibleMetrics.startMinutes * pixelsPerMinute + 1}px`,
    height: `${Math.max(visibleMetrics.durationMinutes * pixelsPerMinute - 2, MIN_BLOCKED_H)}px`,
    left: `${leftPct}%`,
    width: `${widthPct}%`,
  }
  const hatch = (
    <>
      {/* Hatched background */}
      <div
        className="absolute inset-x-0.5 inset-y-0"
        style={{
          backgroundImage:
            'repeating-linear-gradient(135deg, transparent 0px, transparent 4px, rgba(128,128,128,0.07) 4px, rgba(128,128,128,0.07) 8px)',
        }}
      />
      {/* Dashed border */}
      <div className="absolute inset-x-1 inset-y-0 border border-dashed border-border-strong/40 rounded-sm" />
      {/* Label */}
      <div className="relative z-10 h-full flex items-center justify-center px-2">
        <span className="text-[10px] font-medium text-text-tertiary truncate">
          {slot.reason ?? 'Bloqueado'}
        </span>
      </div>
    </>
  )

  return (
    <Tooltip key={slot.id}>
      <TooltipTrigger asChild>
        {isClickable ? (
          <button
            type="button"
            aria-label={ariaLabel}
            onClick={(e) => {
              e.stopPropagation()
              onBlockedSlotClick?.(slot)
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                onBlockedSlotClick?.(slot)
              }
            }}
            className="absolute z-15 pointer-events-auto select-none cursor-pointer rounded-sm hover:brightness-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/60 focus-visible:ring-offset-1"
            style={sharedStyle}
          >
            {hatch}
          </button>
        ) : (
          <div
            role="img"
            aria-label={ariaLabel}
            className="absolute z-15 pointer-events-auto select-none cursor-not-allowed rounded-sm"
            style={sharedStyle}
          >
            {hatch}
          </div>
        )}
      </TooltipTrigger>
      <TooltipContent side="right">
        <p className="font-semibold text-text-primary">{slot.reason ?? 'Horário bloqueado'}</p>
        <p className="tabular-nums text-text-secondary">
          {slot.startTime} – {slot.endTime}
          <span className="ml-1 text-text-tertiary">({friendlyDuration})</span>
        </p>
        {isClickable && <p className="text-[10px] text-text-tertiary pt-0.5">Clique para editar</p>}
        <TooltipArrow />
      </TooltipContent>
    </Tooltip>
  )
}

BlockedSlotCard.displayName = 'BlockedSlotCard'
